import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Loader2, Edit, Download, ImageOff, Tag, Truck, Car } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

const PAGE_SIZE = 10

type TabId = 'sin-foto' | 'sin-precio' | 'sin-proveedor' | 'cobertura'

interface TabDef {
  id: TabId
  label: string
  endpoint: string
  badgeLabel: string
  icon: typeof ImageOff
  badgeVariant: 'danger' | 'warning' | 'info'
}

const TABS: TabDef[] = [
  {
    id: 'sin-foto',
    label: 'Sin foto',
    endpoint: '/api/admin/reportes/productos-sin-foto',
    badgeLabel: 'Productos sin imagen',
    icon: ImageOff,
    badgeVariant: 'danger',
  },
  {
    id: 'sin-precio',
    label: 'Sin precio',
    endpoint: '/api/admin/reportes/productos-sin-precio',
    badgeLabel: 'Productos con precio en $0',
    icon: Tag,
    badgeVariant: 'warning',
  },
  {
    id: 'sin-proveedor',
    label: 'Sin proveedor',
    endpoint: '/api/admin/reportes/productos-sin-proveedor',
    badgeLabel: 'Productos sin proveedor',
    icon: Truck,
    badgeVariant: 'info',
  },
  {
    id: 'cobertura',
    label: 'Cobertura vehículos',
    endpoint: '/api/admin/reportes/cobertura-vehiculos',
    badgeLabel: 'Modelos en el catálogo',
    icon: Car,
    badgeVariant: 'info',
  },
]

interface ProductRow {
  id: string
  sku: string
  nombre: string
  stock: number
  brandNombre: string
  categoriaNombre: string
  problema: string
  precio?: number
  precioOferta?: number | null
  imagen?: string
  supplierId?: string | null
}

interface ProductsReportResponse {
  total: number
  page: number
  pageSize: number
  pages: number
  data: ProductRow[]
}

interface CoberturaRow {
  modeloId: string
  modeloNombre: string
  marcaId: string
  marcaNombre: string
  productos: number
}

interface CoberturaResponse {
  total: number
  page: number
  pageSize: number
  pages: number
  resumen: {
    totalModelos: number
    modelosConProductos: number
    modelosSinProductos: number
  }
  data: CoberturaRow[]
}

function stockBadge(stock: number) {
  if (stock === 0) return <Badge variant="danger">Agotado</Badge>
  if (stock < 5) return <Badge variant="warning">Bajo ({stock})</Badge>
  return <span className="text-sm font-medium text-ink-700">{stock}</span>
}

function downloadCsv(filename: string, rows: Array<Record<string, string | number | null>>) {
  const first = rows[0]
  if (!first) return
  const headers = Object.keys(first)
  const escape = (v: string | number | null | undefined) => {
    if (v === null || v === undefined) return ''
    const s = String(v)
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n')
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function ReportesCalidad() {
  const [activeTab, setActiveTab] = useState<TabId>('sin-foto')
  const [page, setPage] = useState<Record<TabId, number>>({
    'sin-foto': 1,
    'sin-precio': 1,
    'sin-proveedor': 1,
    cobertura: 1,
  })
  const [soloSinProductos, setSoloSinProductos] = useState(false)

  const currentPage = page[activeTab]
  const tab = TABS.find((t) => t.id === activeTab)!

  function setTabPage(id: TabId, value: number) {
    setPage((prev) => ({ ...prev, [id]: value }))
  }

  function changeTab(id: TabId) {
    setActiveTab(id)
  }

  return (
    <>
      <Topbar
        title="Reportes de calidad de catálogo"
        subtitle="Detecta huecos de información en productos y compatibilidades"
      />
      <div className="p-6">
        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-1 rounded-xl border border-ink-200 bg-white p-1">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = t.id === activeTab
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => changeTab(t.id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                <Icon className="size-4" />
                {t.label}
              </button>
            )
          })}
        </div>

        {activeTab === 'cobertura' ? (
          <CoberturaTab
            tab={tab}
            page={currentPage}
            setPage={(p) => setTabPage('cobertura', p)}
            soloSinProductos={soloSinProductos}
            setSoloSinProductos={(v) => {
              setSoloSinProductos(v)
              setTabPage('cobertura', 1)
            }}
          />
        ) : (
          <ProductosTab
            tab={tab}
            page={currentPage}
            setPage={(p) => setTabPage(activeTab, p)}
          />
        )}
      </div>
    </>
  )
}

function ProductosTab({
  tab,
  page,
  setPage,
}: {
  tab: TabDef
  page: number
  setPage: (n: number) => void
}) {
  const navigate = useNavigate()
  const query = useMemo(() => {
    const u = new URLSearchParams()
    u.set('page', String(page))
    u.set('limit', String(PAGE_SIZE))
    return u.toString()
  }, [page])

  const { data, isLoading, isFetching, error } = useQuery<ProductsReportResponse>({
    queryKey: ['reporte', tab.id, page],
    queryFn: () => apiGet(`${tab.endpoint}?${query}`),
    placeholderData: (prev) => prev,
  })

  function exportarCsv() {
    if (!data) return
    // Para exportar todo, pedimos la primera página con limit alto.
    apiGet<ProductsReportResponse>(`${tab.endpoint}?page=1&limit=200`).then((all) => {
      const rows = all.data.map((p) => ({
        sku: p.sku,
        nombre: p.nombre,
        categoria: p.categoriaNombre,
        marca: p.brandNombre,
        stock: p.stock,
        precio: p.precio ?? '',
        problema: p.problema,
      }))
      downloadCsv(`${tab.id}.csv`, rows)
    })
  }

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid size-14 place-items-center rounded-2xl bg-brand-50 text-2xl font-extrabold text-brand-700">
            {data ? data.total.toLocaleString('es-MX') : '—'}
          </div>
          <div>
            <div className="text-sm font-medium text-ink-500">{tab.badgeLabel}</div>
            <div className="mt-0.5">
              <Badge variant={tab.badgeVariant}>{tab.label}</Badge>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={exportarCsv}
          disabled={!data || data.total === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
        >
          <Download className="size-3.5" /> Exportar CSV
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Cargando reporte…</p>
          </div>
        )}

        {error && (
          <div className="p-6 text-sm text-red-700">
            {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="relative overflow-x-auto">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">Nombre</th>
                    <th className="px-4 py-3 font-medium">Categoría</th>
                    <th className="px-4 py-3 font-medium">Marca</th>
                    <th className="px-4 py-3 font-medium">Stock</th>
                    <th className="px-4 py-3 font-medium">Problema</th>
                    <th className="w-20 px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-sm text-ink-500">
                        No se encontraron productos con este problema.
                      </td>
                    </tr>
                  )}
                  {data.data.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-ink-700">{p.sku}</td>
                      <td className="px-4 py-3">
                        <div className="line-clamp-2 max-w-md text-ink-900">{p.nombre}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-700">
                        {p.categoriaNombre || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-700">{p.brandNombre || '—'}</td>
                      <td className="px-4 py-3">{stockBadge(p.stock)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <Badge variant={tab.badgeVariant}>{p.problema}</Badge>
                          {tab.id === 'sin-precio' && (
                            <span className="text-xs text-ink-500">
                              {formatMXN(p.precio ?? 0)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            navigate({
                              to: '/productos/$id/editar',
                              params: { id: p.id },
                            })
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50"
                          title="Editar producto"
                        >
                          <Edit className="size-3.5" /> Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              pages={data.pages}
              total={data.total}
              onPrev={() => setPage(Math.max(1, page - 1))}
              onNext={() => setPage(page + 1)}
              label="productos"
            />
          </>
        )}
      </div>
    </>
  )
}

function CoberturaTab({
  tab,
  page,
  setPage,
  soloSinProductos,
  setSoloSinProductos,
}: {
  tab: TabDef
  page: number
  setPage: (n: number) => void
  soloSinProductos: boolean
  setSoloSinProductos: (v: boolean) => void
}) {
  const query = useMemo(() => {
    const u = new URLSearchParams()
    u.set('page', String(page))
    u.set('limit', String(PAGE_SIZE))
    if (soloSinProductos) u.set('soloSinProductos', '1')
    return u.toString()
  }, [page, soloSinProductos])

  const { data, isLoading, isFetching, error } = useQuery<CoberturaResponse>({
    queryKey: ['reporte', tab.id, page, soloSinProductos],
    queryFn: () => apiGet(`${tab.endpoint}?${query}`),
    placeholderData: (prev) => prev,
  })

  function exportarCsv() {
    if (!data) return
    apiGet<CoberturaResponse>(
      `${tab.endpoint}?page=1&limit=200${soloSinProductos ? '&soloSinProductos=1' : ''}`,
    ).then((all) => {
      const rows = all.data.map((r) => ({
        marca: r.marcaNombre,
        modelo: r.modeloNombre,
        productos: r.productos,
      }))
      downloadCsv('cobertura-vehiculos.csv', rows)
    })
  }

  return (
    <>
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wider text-ink-500">Modelos totales</div>
          <div className="mt-2 text-3xl font-extrabold text-ink-900">
            {data ? data.resumen.totalModelos.toLocaleString('es-MX') : '—'}
          </div>
        </div>
        <div className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wider text-ink-500">Con productos</div>
          <div className="mt-2 text-3xl font-extrabold text-emerald-600">
            {data ? data.resumen.modelosConProductos.toLocaleString('es-MX') : '—'}
          </div>
        </div>
        <div className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wider text-ink-500">Sin productos</div>
          <div className="mt-2 text-3xl font-extrabold text-red-600">
            {data ? data.resumen.modelosSinProductos.toLocaleString('es-MX') : '—'}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={soloSinProductos}
            onChange={(e) => setSoloSinProductos(e.target.checked)}
            className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
          />
          Solo modelos sin productos
        </label>

        <button
          type="button"
          onClick={exportarCsv}
          disabled={!data || data.total === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
        >
          <Download className="size-3.5" /> Exportar CSV
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Cargando reporte…</p>
          </div>
        )}

        {error && (
          <div className="p-6 text-sm text-red-700">
            {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="relative overflow-x-auto">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Marca</th>
                    <th className="px-4 py-3 font-medium">Modelo</th>
                    <th className="px-4 py-3 font-medium text-right">Productos</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-sm text-ink-500">
                        Sin resultados.
                      </td>
                    </tr>
                  )}
                  {data.data.map((r) => (
                    <tr
                      key={r.modeloId}
                      className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                    >
                      <td className="px-4 py-3 text-ink-900">{r.marcaNombre}</td>
                      <td className="px-4 py-3 text-ink-900">{r.modeloNombre}</td>
                      <td className="px-4 py-3 text-right font-semibold text-ink-900">
                        {r.productos.toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-3">
                        {r.productos === 0 ? (
                          <Badge variant="danger">Sin productos</Badge>
                        ) : r.productos < 5 ? (
                          <Badge variant="warning">Cobertura baja</Badge>
                        ) : (
                          <Badge variant="success">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              pages={data.pages}
              total={data.total}
              onPrev={() => setPage(Math.max(1, page - 1))}
              onNext={() => setPage(page + 1)}
              label="modelos"
            />
          </>
        )}
      </div>
    </>
  )
}

function Pagination({
  page,
  pages,
  total,
  onPrev,
  onNext,
  label,
}: {
  page: number
  pages: number
  total: number
  onPrev: () => void
  onNext: () => void
  label: string
}) {
  return (
    <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
      <div className="text-ink-500">
        Página <strong className="text-ink-900">{page}</strong> de{' '}
        <strong className="text-ink-900">{pages.toLocaleString('es-MX')}</strong> (
        {total.toLocaleString('es-MX')} {label})
      </div>
      <div className="flex gap-1">
        <button
          onClick={onPrev}
          disabled={page === 1}
          className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
        >
          Anterior
        </button>
        <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
        <button
          onClick={onNext}
          disabled={page >= pages}
          className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
        >
          Siguiente
        </button>
      </div>
    </div>
  )
}

export default ReportesCalidad
